<template>
    <div class="am-multi" :class="{ 'am-multi--disabled': disabled }">
        <div class="am-multi__chips" v-if="selectedOptions.length">
            <button
                v-for="opt in selectedOptions"
                :key="String(opt.value)"
                type="button"
                class="am-multi__chip"
                :disabled="disabled"
                @click="remove(opt.value)"
            >
                <span>{{ opt.label }}</span>
                <span class="am-multi__chip-x" aria-hidden="true">×</span>
            </button>
        </div>

        <div ref="fieldRef" class="am-multi__field">
            <input
                v-model="searchQuery"
                type="text"
                class="am-multi__search"
                :placeholder="placeholder"
                :disabled="disabled || limitReached"
                @focus="openMenu"
                @keydown.down.prevent="openMenu"
            />
            <span v-if="limit > 0" class="am-multi__counter">
                {{ modelValue.length }}/{{ limit }}
            </span>
        </div>

        <Teleport to="body">
            <div
                v-if="isOpen && !disabled"
                ref="menuRef"
                class="am-multi__dropdown"
                role="listbox"
                :style="menuStyle"
                @mousedown.prevent
            >
                <button
                    v-for="opt in filteredOptions"
                    :key="String(opt.value)"
                    type="button"
                    role="option"
                    class="am-multi__option"
                    :class="{
                        'am-multi__option--selected': isSelected(opt.value),
                        'am-multi__option--disabled': !isSelected(opt.value) && limitReached,
                    }"
                    :disabled="!isSelected(opt.value) && limitReached"
                    :aria-selected="isSelected(opt.value)"
                    @click="toggle(opt.value)"
                >
                    <span class="am-multi__check" aria-hidden="true">{{ isSelected(opt.value) ? '✓' : '' }}</span>
                    {{ opt.label }}
                </button>
                <p v-if="filteredOptions.length === 0" class="am-multi__empty">Ничего не найдено</p>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'

const props = defineProps({
    modelValue: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Поиск и выбор…' },
    disabled: { type: Boolean, default: false },
    /** 0 = без лимита */
    limit: { type: Number, default: 0 },
})

const emit = defineEmits(['update:modelValue'])

const fieldRef = ref(null)
const menuRef = ref(null)
const isOpen = ref(false)
const searchQuery = ref('')
const menuStyle = reactive({ position: 'fixed', left: '0px', top: '0px', width: '0px', zIndex: 2000 })

const limitReached = computed(() => props.limit > 0 && props.modelValue.length >= props.limit)

function updatePosition() {
    const el = fieldRef.value
    if (!el) return
    const r = el.getBoundingClientRect()
    const margin = 8
    const below = window.innerHeight - r.bottom
    const maxHeight = 280
    // Если снизу мало места, раскрываем вверх.
    if (below < maxHeight + margin && r.top > below) {
        menuStyle.top = `${Math.max(margin, r.top - Math.min(maxHeight, r.top - margin) - 4)}px`
    } else {
        menuStyle.top = `${r.bottom + 4}px`
    }
    menuStyle.left = `${r.left}px`
    menuStyle.width = `${r.width}px`
}

function openMenu() {
    if (props.disabled) return
    isOpen.value = true
    nextTick(updatePosition)
}

function closeMenu() {
    isOpen.value = false
}

const selectedOptions = computed(() => {
    const set = new Set(props.modelValue.map(String))
    return props.options.filter(o => set.has(String(o.value)))
})

const filteredOptions = computed(() => {
    const q = searchQuery.value.trim().toLowerCase()
    if (!q) return props.options
    return props.options.filter(o => String(o.label).toLowerCase().includes(q))
})

function isSelected(value) {
    return props.modelValue.map(String).includes(String(value))
}

function toggle(value) {
    const key = String(value)
    const current = props.modelValue.map(String)
    if (current.includes(key)) {
        emit('update:modelValue', props.modelValue.filter(v => String(v) !== key))
        return
    }
    if (limitReached.value) return
    emit('update:modelValue', [...props.modelValue, value])
}

function remove(value) {
    emit('update:modelValue', props.modelValue.filter(v => String(v) !== String(value)))
}

function onDocClick(e) {
    const inField = fieldRef.value?.contains(e.target)
    const inMenu = menuRef.value?.contains(e.target)
    if (!inField && !inMenu) closeMenu()
}

function bindViewportListeners() {
    // capture=true — ловим скролл контейнеров (тело модалки), а не только window.
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
}
function unbindViewportListeners() {
    window.removeEventListener('scroll', updatePosition, true)
    window.removeEventListener('resize', updatePosition)
}

watch(isOpen, (open) => {
    if (open) {
        document.addEventListener('mousedown', onDocClick)
        bindViewportListeners()
    } else {
        document.removeEventListener('mousedown', onDocClick)
        unbindViewportListeners()
    }
})

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onDocClick)
    unbindViewportListeners()
})
</script>

<style scoped>
.am-multi {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.am-multi--disabled {
    opacity: 0.55;
    pointer-events: none;
}
.am-multi__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.am-multi__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 999px;
    border: 1px solid #e0e0e0;
    background: #f7f8f9;
    font-family: inherit;
    font-size: 12.5px;
    color: #23282C;
    cursor: pointer;
}
.am-multi__chip:hover {
    border-color: #E30613;
    color: #E30613;
}
.am-multi__chip-x {
    font-size: 14px;
    line-height: 1;
    opacity: 0.7;
}
.am-multi__field {
    position: relative;
}
.am-multi__search {
    width: 100%;
    box-sizing: border-box;
    font-family: inherit;
    font-size: 13.5px;
    padding: 10px 56px 10px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    background: white;
    color: #23282C;
}
.am-multi__search:focus {
    outline: 0;
    border-color: #23282C;
}
.am-multi__counter {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11.5px;
    color: #888;
}
.am-multi__dropdown {
    max-height: 280px;
    overflow-y: auto;
    background: white;
    border: 1px solid #e7e7e7;
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(21, 26, 34, 0.18);
    padding: 4px;
}
.am-multi__option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    text-align: left;
    border: 0;
    background: transparent;
    padding: 8px 10px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: #23282C;
    cursor: pointer;
}
.am-multi__option:hover {
    background: #f3f4f5;
}
.am-multi__option--selected {
    background: #FFF0F1;
    color: #C0050F;
    font-weight: 600;
}
.am-multi__option--disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
.am-multi__check {
    width: 14px;
    text-align: center;
    font-size: 12px;
}
.am-multi__empty {
    margin: 0;
    padding: 12px;
    font-size: 12.5px;
    color: #888;
    text-align: center;
}
</style>
